<template>
  <div>
    <div class="clearfix mb-3">
      <div class="float-start">
        <h4 class="card-title">
          {{ $t('notification.setting.emailNotification.setNotification') }}
        </h4>
      </div>
      <div class="float-end d-flex">
        <McButton
          v-if="!isAdding"
          variant="secondary"
          :text="$t('global.cancel')"
          icon="icon-outline_close"
          class="mx-2 mb-2 ms-auto"
          :disabled="!hasChanged"
          @click="handleReset"
        />
        <McButton
          v-if="!isAdding"
          variant="primary"
          class="mb-2 ms-auto"
          :text="$t('global.save')"
          icon="icon-outline_save"
          :disabled="!hasChanged"
          @click="handleSubmit"
        />
      </div>
    </div>
    <b-card v-if="isAdding">
      <McEmptyState
        :title="$t('notification.setting.emptyState.notificationType')"
        :desc="$t('notification.setting.emptyState.desc')"
        :hasButton="false"
        class="pb-4"
        placementType="grid"
      />
    </b-card>
    <div v-else>
      <b-card>
        <div class="mc-notification-tabs">
          <b-button
            v-for="(tab, idx) in tabs"
            :key="idx"
            variant="light"
            :disabled="tab.isDisabled"
            class="position-relative"
            :class="{ active: tab.key == activeTab }"
            @click="handleChangeTab(tab.key)"
          >
            {{ tab.title }}
          </b-button>
        </div>
        <b-skeleton-wrapper :loading="!rendered">
          <template #loading>
            <b-card>
              <b-skeleton width="65%" height="300px" class="my-2" />
            </b-card>
          </template>

          <!-- General Notif Tab -->
          <notification-tab
            ref="generalRefs"
            :active-tab="activeTab"
            tab-name="general"
            :title="$t('global.alert.type.general')"
            :items="listGeneral"
            :handle-setting="handleSetting"
            :selected="selectedGeneralIds"
            @handleAlertCode="getGeneralAlert"
          />
          <!-- DMS Notif Tab -->
          <notification-tab
            ref="dmsRefs"
            :active-tab="activeTab"
            tab-name="dms"
            :title="$t('global.alert.type.dms')"
            :items="listDms"
            :handle-setting="handleSetting"
            :selected="selectedDmsIds"
            @handleAlertCode="getDmsAlert"
          />
          <!-- Driver Behavior Notif Tab -->
          <notification-tab
            ref="driverRefs"
            :active-tab="activeTab"
            tab-name="driver"
            :title="$t('global.alert.type.driverBehavior')"
            :items="listDriver"
            :handle-setting="handleSetting"
            :selected="selectedDriverIds"
            @handleAlertCode="getDriverAlert"
          />
          <!-- ADAS Notif Tab -->
          <notification-tab
            v-if="hasAccess(['e1g-web-adas:read'])"
            ref="adasRefs"
            :active-tab="activeTab"
            tab-name="adas"
            :title="$t('global.alert.type.adas')"
            :items="listAdas"
            :handle-setting="handleSetting"
            :selected="selectedAdasIds"
            @handleAlertCode="getAdasAlert"
          />
        </b-skeleton-wrapper>

        <h5 class="card-title">
          {{ $t('notification.setting.emailNotification.sentWhen') }}
        </h5>
        <div class="form-check-left form-switch my-3">
          <input
            id="moreinfo-history"
            v-model="form.showHideGeolocation"
            class="form-check-input me-2"
            type="checkbox"
            @click="form.showHideGeolocation = !form.showHideGeolocation"
          />
          <span class="" for="moreinfo-history">
            {{ $t('notification.setting.emailNotification.geoBased') }}
          </span>
        </div>
        <div v-if="form.showHideGeolocation">
          <b-row class="my-2">
            <b-col md="4" class="d-flex align-self-center">
              <McRadioButton
                id="inside"
                key="inside"
                v-model="form.geofenceType"
                initialValue="inside"
                @change="setGeofenceObj('inside')"
              >
                <template #label>
                  <label class="form-check-label">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <span v-html="$t('notification.setting.emailNotification.insideGeo')" />
                  </label>
                </template>
              </McRadioButton>
            </b-col>
            <b-col md="4">
              <FormulateInput
                v-model="form.insideGeofenceId"
                type="geofence-value-help"
                name="insideGeofence"
                trackBy="geofenceId"
                :placeholder="`${$t('global.form.select')} ${$t('global.object.geolocation')}`"
                displayedBy="fenceName"
                :fullObject="insideObj"
                :listGetter="queryGeofence.get"
                :multiple="true"
                :tableFields="fieldGeofence"
                :modalTitle="`${$t('global.form.select')} ${$t('global.object.geolocation')}`"
                :disabled="form.geofenceType !== 'inside'"
                :geofenceAllData="geofenceAllData"
                @input:exception="handleException"
                @update:fullObject="insideObj = $event"
              />
            </b-col>
          </b-row>
          <b-row class="my-2">
            <b-col md="4" class="d-flex align-self-center">
              <McRadioButton
                id="outside"
                key="outside"
                v-model="form.geofenceType"
                initialValue="outside"
                @change="setGeofenceObj('outside')"
              >
                <template #label>
                  <label class="form-check-label">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <span v-html="$t('notification.setting.emailNotification.outsideGeo')" />
                  </label>
                </template>
              </McRadioButton>
            </b-col>
            <b-col md="4">
              <FormulateInput
                v-model="form.outsideGeofenceId"
                type="geofence-value-help"
                name="outsideGeofence"
                trackBy="geofenceId"
                :placeholder="`${$t('global.form.select')} ${$t('global.object.geolocation')}`"
                displayedBy="fenceName"
                :fullObject="outsideObj"
                :listGetter="queryGeofence.get"
                :multiple="true"
                :tableFields="fieldGeofence"
                :modalTitle="`${$t('global.form.select')} ${$t('global.object.geolocation')}`"
                :disabled="form.geofenceType !== 'outside'"
                :geofenceAllData="geofenceAllData"
                @input:exception="handleException"
                @update:fullObject="outsideObj = $event"
              />
            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import McRadioButton from '@/components/McRadioButton'
import McEmptyState from '@/components/McEmptyState'
import NotificationTab from './NotificationTab.vue'
import { general, dms, driverBehavior, adas } from '../lib/alert-code'
import { GET_ADDRESS } from '@/services/api/master/address'
import { UPDATE_NOTIFICATION_GROUP } from '@/services/api/notification/ngroup'
import { mapGetters } from 'vuex'

export default {
  components: {
    McButton,
    McRadioButton,
    McEmptyState,
    NotificationTab,
  },
  props: {
    isAdding: {
      type: Boolean,
      default: false,
    },
    ngroupId: {
      type: Number,
      default: 0,
    },
    item: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      hasData: false,
      activeTab: 'general',
      generalIds: [],
      dmsIds: [],
      driverIds: [],
      adasIds: [],
      selectedGeneralIds: [],
      selectedDmsIds: [],
      selectedDriverIds: [],
      selectedAdasIds: [],
      form: {
        alertCode: [],
        geofenceType: 'inside',
        showHideGeolocation: false,
        insideGeofenceId: [],
        outsideGeofenceId: [],
      },
      oldData: {
        alertCode: [],
        geofenceType: 'inside',
        showHideGeolocation: false,
        insideGeofenceId: [],
        outsideGeofenceId: [],
      },
      insideObj: [],
      outsideObj: [],
      rendered: true,
      geofenceSelectionMode: 'Selection',
      exception: {},
      geofenceAllData: [],
      globalSearch: '',
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    tabs() {
      const tab = [
        { key: 'general', title: this.$t('global.alert.type.general'), isDisabled: false },
        { key: 'dms', title: this.$t('global.alert.type.dms'), isDisabled: false },
        { key: 'driver', title: this.$t('global.alert.type.driverBehavior'), isDisabled: false },
      ]
      if (this.hasAccess(['e1g-web-adas:read'])) {
        tab.push({ key: 'adas', title: this.$t('global.alert.type.adas'), isDisabled: false })
      }
      return tab
    },
    listGeneral() {
      const data = general
      data.sort((a, b) => {
        let x = a.title.toLowerCase()
        let y = b.title.toLowerCase()
        if (x < y) {
          return -1
        }
        if (x > y) {
          return 1
        }
        return 0
      })
      return data
    },
    listDms() {
      const data = dms
      data.sort((a, b) => {
        let x = a.title.toLowerCase()
        let y = b.title.toLowerCase()
        if (x < y) {
          return -1
        }
        if (x > y) {
          return 1
        }
        return 0
      })
      return data
    },
    listDriver() {
      const data = driverBehavior
      data.sort((a, b) => {
        let x = a.title.toLowerCase()
        let y = b.title.toLowerCase()
        if (x < y) {
          return -1
        }
        if (x > y) {
          return 1
        }
        return 0
      })
      return data
    },
    listAdas() {
      const data = adas
      data.sort((a, b) => {
        let x = a.title.toLowerCase()
        let y = b.title.toLowerCase()
        if (x < y) {
          return -1
        }
        if (x > y) {
          return 1
        }
        return 0
      })
      return data
    },
    queryGeofence() {
      return {
        get: async (opts) => {
          let result = {}

          const self = this
          self.globalSearch = opts.search

          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            orderBy: opts.sorts,
            search: opts.search,
          }

          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_ADDRESS,
            variables: {
              filter: filter,
            },
          })

          const data = res.data.geofences.geofences

          result = {
            metadata: {
              total_count: data.pagination.total,
              page: data.pagination.page,
            },
            data: {
              paginated_result: data.data,
            },
          }

          return result
        },
      }
    },
    fieldGeofence() {
      return [
        {
          key: 'fenceName',
          label: this.$t('master.form.address.name'),
          isSortable: true,
        },
        {
          key: 'province',
          label: this.$t('master.list.address.province'),
          isSortable: true,
        },
        {
          key: 'city',
          label: this.$t('master.list.address.city'),
          isSortable: true,
        },
      ]
    },
    hasChanged() {
      const res = this.filterUnchangedData(this.form, this.oldData)
      return res[1]
    },
  },
  watch: {
    globalSearch: function (val) {
      this.searchData(val)
    },
  },
  async mounted() {
    if (!this.isAdding && this.item.alertCode) await this.initData()
  },

  methods: {
    async searchData(val) {
      const res = await this.$apollo.query({
        fetchPolicy: 'no-cache',
        query: GET_ADDRESS,
        variables: {
          filter: {
            pagination: {
              page: 1,
              show: 9999,
            },
            search: val,
          },
        },
      })

      this.geofenceAllData = res.data.geofences.geofences.data
    },

    async initData() {
      this.rendered = false
      const res = await this.$apollo.query({
        fetchPolicy: 'no-cache',
        query: GET_ADDRESS,
        variables: {
          filter: {
            pagination: {
              page: 1,
              show: 9999,
            },
          },
        },
      })

      this.geofenceAllData = res.data.geofences.geofences.data
      this.selectedGeneralIds = this.filterIds(this.listGeneral)
      this.selectedDmsIds = this.filterIds(this.listDms)
      this.selectedDriverIds = this.filterIds(this.listDriver)
      this.selectedAdasIds = this.filterIds(this.listAdas)

      const item = this.item
      this.form.alertCode = item.alertCode.sort((a, b) => a - b)
      this.rendered = true
      if (this.item.geofence.length > 0) {
        const data = res.data.geofences.geofences.data
        const inside = this.item.geofence.every((item) => item.type === 'Inside')
        this.form.showHideGeolocation = true
        if (inside) {
          this.form.insideGeofenceId = this.item.geofence.map((item) => item.geofenceId)
          this.form.geofenceType = 'inside'
          this.insideObj = data.filter((item) =>
            this.form.insideGeofenceId.includes(item.geofenceId)
          )
        } else {
          this.form.outsideGeofenceId = this.item.geofence.map((item) => item.geofenceId)
          this.form.geofenceType = 'outside'
          this.outsideObj = data.filter((item) =>
            this.form.outsideGeofenceId.includes(item.geofenceId)
          )
        }
      }
      this.oldData = {
        alertCode: this.form.alertCode,
        geofenceType: this.form.geofenceType,
        showHideGeolocation: this.form.showHideGeolocation,
        insideGeofenceId: this.form.insideGeofenceId ? this.form.insideGeofenceId : [],
        outsideGeofenceId: this.form.outsideGeofenceId ? this.form.outsideGeofenceId : [],
      }
    },
    filterIds(data) {
      return data.reduce((acc, item) => {
        if (item.alertCode.some((code) => this.item.alertCode.includes(code))) {
          acc.push(item.id)
        }
        return acc
      }, [])
    },
    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}
      keys.forEach((key) => {
        if (oldValue[key] == newValue[key]) return
        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)

      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    handleChangeTab(value) {
      this.activeTab = value
    },
    addHandler() {
      this.hasData = true
    },
    handleSetting(url) {
      let route = this.$router.resolve({ name: url })
      window.open(route.href, '_blank')
    },
    getGeneralAlert(value) {
      this.generalIds = value
      this.mergeAlertCode()
    },
    getDmsAlert(value) {
      this.dmsIds = value
      this.mergeAlertCode()
    },
    getDriverAlert(value) {
      this.driverIds = value
      this.mergeAlertCode()
    },
    getAdasAlert(value) {
      this.adasIds = value
      this.mergeAlertCode()
    },
    mergeAlertCode() {
      const alert = [...this.generalIds, ...this.dmsIds, ...this.driverIds, ...this.adasIds]
      this.form.alertCode = alert.sort((a, b) => a - b)
    },
    setGeofenceObj(params) {
      if (params == 'inside') {
        this.form.outsideGeofenceId = []
        this.outsideObj = {}
      } else {
        this.form.insideGeofenceId = []
        this.insideObj = {}
      }
    },
    handleReset() {
      this.form = {
        geofenceType: this.oldData.geofenceType,
        insideGeofenceId: this.oldData.insideGeofenceId ? this.oldData.insideGeofenceId : [],
        outsideGeofenceId: this.oldData.outsideGeofenceId ? this.oldData.outsideGeofenceId : [],
        showHideGeolocation: this.oldData.showHideGeolocation,
        alertCode: this.oldData.alertCode,
      }
      this.$refs.generalRefs.initData()
      this.$refs.dmsRefs.initData()
      this.$refs.driverRefs.initData()
      this.$refs.adasRefs.initData()
    },
    handleException(value) {
      if (value.exclude.length >= 0 && value.selectAll) {
        this.geofenceSelectionMode = 'Exclude'
      } else {
        this.geofenceSelectionMode = 'Selection'
      }
      this.exception = value
    },
    async handleSubmit() {
      const input = {
        ngroupId: Number(this.ngroupId),
        alertCode: [...this.generalIds, ...this.dmsIds, ...this.driverIds, ...this.adasIds],
        insideGeofenceId: [],
        outsideGeofenceId: [],
        geofenceSelectionMode: this.geofenceSelectionMode,
        excludeCustomerGeofence: true,
      }

      if (this.form.showHideGeolocation) {
        if (this.geofenceSelectionMode == 'Exclude') {
          const exclude = this.exception.exclude.map((item) => item.geofenceId)
          if (this.form.geofenceType == 'inside') {
            input.insideGeofenceId = exclude
            input.outsideGeofenceId = null
          } else {
            input.insideGeofenceId = null
            input.outsideGeofenceId = exclude
          }
        } else {
          if (this.form.geofenceType == 'inside') {
            input.insideGeofenceId = this.form.insideGeofenceId.map((id) => parseInt(id))
            input.outsideGeofenceId = null
          } else {
            input.insideGeofenceId = null
            input.outsideGeofenceId = this.form.outsideGeofenceId.map((id) => parseInt(id))
          }
        }
      }

      const res = await this.$apollo.mutate({
        mutation: UPDATE_NOTIFICATION_GROUP,
        variables: {
          input: input,
        },
      })
      if (res.data.updateNgroup.isSuccessful) {
        this.$mcToast.success(this.$t('global.alert.text.changed'))
      } else {
        this.$mcToast.error(res.data.updateNgroup.message)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-notification-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  margin-bottom: 32px;

  & > button {
    height: 36px;
    flex-shrink: 0;
    color: $mep-color-text;
    font-weight: 500;
    transition: none;
    background: none;
    outline: none;
    border: none;
    border-radius: 4px;
    padding: 8px 20px;

    &:hover {
      background-color: $mep-secondary-100;
      color: $mep-color-text-400;
    }

    &:focus {
      outline: none;
      box-shadow: none;
    }

    &:disabled {
      color: #67718b;
    }
  }

  & > button.active {
    border: 1px solid $mep-primary;
    background-color: $mep-secondary-100;
    color: $mep-primary;
    font-weight: 600;
  }
}

.form-check-label {
  padding: 0px 0px 0px 8px;
  text-wrap: pretty;
}
.custom-radio {
  display: flex;
  align-self: center;
  .custom-control-input {
    display: flex;
    align-self: center;
  }
}
</style>
